import { useEffect, useState } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import {
  Alert, Box, Button, Card, CardContent, CircularProgress, Stack, TextField,
} from "@mui/material";
import { api, errorMessage, fieldErrors } from "@/api/client";
import { endpoints } from "@/api/endpoints";
import { useAuth, type AuthResult } from "@/store/auth";
import { useThemePref } from "@/store/theme";
import { BrandLogo } from "@/components/BrandMark";

interface LoginForm {
  email: string;
  password: string;
}

export function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();

  const isAuthenticated = useAuth((s) => s.isAuthenticated());
  const setSession = useAuth((s) => s.setSession);
  const sessionExpired = useAuth((s) => s.sessionExpired);
  const setSessionExpired = useAuth((s) => s.setSessionExpired);
  const setTenantPolicy = useThemePref((s) => s.setTenantPolicy);

  const [submitError, setSubmitError] = useState<string | null>(null);

  const {
    register, handleSubmit, setError,
    formState: { errors, isSubmitting },
  } = useForm<LoginForm>({ defaultValues: { email: "", password: "" } });

  // Clear the expiry notice once seen, so it does not reappear on a later deliberate sign-in.
  useEffect(() => () => setSessionExpired(false), [setSessionExpired]);

  if (isAuthenticated) {
    const from = (location.state as { from?: string } | null)?.from;
    return <Navigate to={from ?? "/"} replace />;
  }

  const onSubmit = handleSubmit(async (values) => {
    setSubmitError(null);
    try {
      const { data } = await api.post<AuthResult>(endpoints.auth.login, values);
      setSession(data);

      // The tenant's house palette travels with the login response, so the first painted screen
      // is already in the right colours rather than flashing the default first.
      setTenantPolicy(data.user.themeId, data.user.allowThemeOverride);

      const from = (location.state as { from?: string } | null)?.from;
      navigate(from ?? "/", { replace: true });
    } catch (error) {
      // Field errors go on the fields; anything else becomes the banner. The API deliberately
      // returns the same message for a wrong password and an unknown address, so this cannot be
      // used to find out who works at the hospital.
      const fields = fieldErrors(error);
      const known = Object.entries(fields).filter(([name]) => name === "email" || name === "password");

      if (known.length > 0) {
        for (const [name, message] of known) {
          setError(name as keyof LoginForm, { message });
        }
      } else {
        setSubmitError(errorMessage(error));
      }
    }
  });

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "grid",
        placeItems: "center",
        bgcolor: "background.default",
        p: 2,
      }}
    >
      <Card sx={{ width: "100%", maxWidth: 420 }} elevation={0} variant="outlined">
        <CardContent sx={{ p: { xs: 3, sm: 4 } }}>
          <Stack spacing={3}>
            <BrandLogo />

            {sessionExpired && (
              <Alert severity="info">Your session timed out. Please sign in again.</Alert>
            )}
            {submitError && <Alert severity="error">{submitError}</Alert>}

            <form onSubmit={onSubmit} noValidate>
              <Stack spacing={2}>
                <TextField
                  label="Email"
                  type="email"
                  autoComplete="username"
                  autoFocus
                  fullWidth
                  error={Boolean(errors.email)}
                  helperText={errors.email?.message}
                  {...register("email", { required: "Enter your email address." })}
                />
                <TextField
                  label="Password"
                  type="password"
                  autoComplete="current-password"
                  fullWidth
                  error={Boolean(errors.password)}
                  helperText={errors.password?.message}
                  {...register("password", { required: "Enter your password." })}
                />
                <Button
                  type="submit"
                  variant="contained"
                  size="large"
                  disabled={isSubmitting}
                  startIcon={isSubmitting ? <CircularProgress size={16} color="inherit" /> : null}
                >
                  {isSubmitting ? "Signing in…" : "Sign in"}
                </Button>
              </Stack>
            </form>
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );
}
